/**
 * zarvia-atc.js
 * Quick Add AJAX cart, ATC popup, bundle add-to-cart, sticky nav.
 */

(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);

  // Money: handles both Shopify endpoint price formats
  // /products/{handle}.js  -> integer minor units e.g. 149900
  // /collections/{handle}/products.json -> decimal string e.g. "1499.00"
  const money = (raw) => {
    if (raw === undefined || raw === null || raw === '') return '';
    const str = String(raw).trim();
    if (!str) return '';
    const num = Number(str);
    if (isNaN(num)) return '';
    /* Decimal string = already in major currency units (Rs.1499.00) */
    /* Plain integer  = minor units, divide by 100 (149900 → Rs.1499) */
    const amount = str.includes('.') ? Math.round(num) : Math.round(num / 100);
    return amount > 0 ? `Rs.${amount.toLocaleString('en-PK')}` : '';
  };

  // Image src: handles both Shopify endpoint formats
  // /products/{handle}.js -> featured_image is a string URL
  // /collections/{handle}/products.json -> image.src or images[0].src
  const getProductSrc = (p) => {
    let src = '';
    if (p.featured_image) {
      src = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image?.src || '');
    } else if (p.image?.src) {
      src = p.image.src;
    } else if (p.images?.[0]?.src) {
      src = p.images[0].src;
    }
    if (!src) return '';
    if (src.startsWith('//')) src = `https:${src}`;
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}width=240`;
  };

  // Price: handles both Shopify endpoint formats
  const getProductPrice = (p) => {
    // /products/{handle}.js -> p.price is integer cents
    if (p.price !== undefined && p.price !== null) return money(p.price);
    // /collections/{handle}/products.json -> price is on variants
    if (p.variants?.[0]?.price !== undefined) return money(p.variants[0].price);
    return '';
  };

  /* ──────────────────────────────────────────────────────────────────
     1. CART HELPERS
  ────────────────────────────────────────────────────────────────── */

  async function addItemsToCart(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error('Cart error');
    return res.json();
  }

  /* Refresh the visible cart UI after AJAX add.
     Cart bubble = <a id="cart-icon-bubble"> inside header section.
     Cart drawer  = <div id="CartDrawer"> inside snippets/cart-drawer.liquid.
     Both are NOT standalone sections — fetch /cart?sections= to get fresh HTML. */
  async function refreshCartUI() {
    try {
      /* 1. Fresh cart data for the count */
      const cartData = await (await fetch('/cart.js')).json();
      const count = cartData.item_count || 0;

      /* 2. Update count bubble directly (reliable regardless of DOM structure) */
      document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
        const spans = bubble.querySelectorAll('span');
        if (spans[0]) spans[0].textContent = count;
        if (spans[1]) spans[1].textContent = `${count} items`;
        /* Show/hide the bubble */
        if (count > 0) {
          bubble.removeAttribute('hidden');
          bubble.classList.remove('hidden');
        } else {
          bubble.setAttribute('hidden', '');
        }
      });

      /* 3. Re-render full cart drawer via Shopify Section Rendering API */
      const drawerResp = await fetch('/cart?sections=cart-drawer');
      if (drawerResp.ok) {
        const sections = await drawerResp.json();
        if (sections['cart-drawer']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const newContent = doc.querySelector('#CartDrawer');
          const currentEl  = document.getElementById('CartDrawer');
          if (newContent && currentEl) {
            currentEl.innerHTML = newContent.innerHTML;
            /* Rebind overlay close — replacing innerHTML detaches Dawn's listener */
            bindCartOverlay();
            /* Remove is-empty from the outer <cart-drawer> element — Dawn normally
               does this inside renderContents() but we bypass that path */
            const drawerEl = document.querySelector('cart-drawer');
            const drawerItems = document.querySelector('cart-drawer-items');
            if (drawerEl) {
              if (count > 0) {
                drawerEl.classList.remove('is-empty');
                if (drawerItems) drawerItems.classList.remove('is-empty');
              } else {
                drawerEl.classList.add('is-empty');
                if (drawerItems) drawerItems.classList.add('is-empty');
              }
            }
          }
        }
      }
    } catch {}
  }

  /* Bind #CartDrawer-Overlay click to close the drawer.
     Called on load AND after every innerHTML refresh (Dawn's listener gets detached). */
  function bindCartOverlay() {
    const overlay  = document.getElementById('CartDrawer-Overlay');
    const drawerEl = document.querySelector('cart-drawer');
    if (!overlay || !drawerEl) return;
    // Replace onclick to avoid stacking duplicate listeners
    overlay.onclick = () => {
      if (typeof drawerEl.close === 'function') drawerEl.close();
    };
  }

  // Bind on initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCartOverlay);
  } else {
    bindCartOverlay();
  }

  /* ──────────────────────────────────────────────────────────────────
     2. POPUP
  ────────────────────────────────────────────────────────────────── */

  const popup       = $('#zr-atc-popup');
  const backdrop    = $('#zr-atc-backdrop');
  const closeBtn    = $('#zr-atc-close');
  const continueBtn = $('#zr-atc-continue');
  const productSlot = $('#zr-atc-product');
  const upsellWrap  = $('#zr-atc-upsell');
  const upsellGrid  = $('#zr-atc-upsell-grid');
  const offerBlock  = popup?.querySelector('.zr-atc-popup__offer');

  function openPopup(mode = 'normal') {
    if (!popup) return;
    popup.dataset.mode = mode;
    if (offerBlock) {
      offerBlock.hidden = mode === 'gift';
      offerBlock.dataset.bundleMode = mode === 'bundle' ? '1' : '';
    }
    popup.hidden = false;
    document.body.style.overflow = 'hidden';
    closeBtn?.focus();
  }

  function closePopup() {
    if (!popup) return;
    popup.hidden = true;
    popup.dataset.mode = '';
    document.body.style.overflow = '';
  }

  popup?.addEventListener('click', (e) => { if (e.target === popup) closePopup(); });

  function fillProductSlot(src, title, price, compare) {
    if (!productSlot) return;
    productSlot.hidden = false;
    productSlot.innerHTML = `
      ${src ? `<img class="zr-atc-popup__product-img" src="${src}" alt="${title}" loading="lazy">` : ''}
      <div>
        <p class="zr-atc-popup__product-title">${title}</p>
        <div>
          <span class="zr-atc-popup__product-price">${price}</span>
          ${compare && compare !== price ? `<span class="zr-atc-popup__product-compare">${compare}</span>` : ''}
        </div>
      </div>`;
  }

  function fillBundleSlot(count) {
    if (!productSlot) return;
    productSlot.hidden = false;
    productSlot.innerHTML = `
      <div style="padding:.4rem 0;">
        <p class="zr-atc-popup__product-title">${count} piece${count > 1 ? 's' : ''} added to your bag</p>
        <p style="font-size:1.25rem;color:var(--zr-ink-2);margin-top:.4rem;">Mix &amp; match — discount at checkout.</p>
      </div>`;
  }

  /* ──────────────────────────────────────────────────────────────────
     2b. FREE GIFT (order note/attribute at spend threshold — no product)
  ────────────────────────────────────────────────────────────────── */

  const giftConfig = window.zrGiftConfig || {};
  const GIFT_ATTR_KEY = 'Free Gift Earring';
  let giftCheckBusy = false;

  function showGiftPopup() {
    const titleEl = document.getElementById('zr-atc-popup-title');
    if (titleEl) {
      titleEl.innerHTML = `
        <span class="zr-atc-popup__success-icon" aria-hidden="true">🎁</span>
        ${giftConfig.message || "Yay! Great, you've got a Special Surprise Earring as a gift on this order!"}
      `;
    }
    if (productSlot) { productSlot.hidden = true; productSlot.innerHTML = ''; }
    upsellWrap && (upsellWrap.hidden = true);
    upsellGrid && (upsellGrid.innerHTML = '');
    openPopup('gift');
  }

  async function setGiftAttribute(unlocked) {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        attributes: { [GIFT_ATTR_KEY]: unlocked ? giftConfig.orderNote || 'Yes' : '' },
      }),
    });
  }

  async function checkFreeGift() {
    if (!giftConfig.goalCents || giftCheckBusy) return;
    giftCheckBusy = true;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const alreadyUnlocked = !!cart.attributes?.[GIFT_ATTR_KEY];

      if (cart.total_price >= giftConfig.goalCents && !alreadyUnlocked) {
        await setGiftAttribute(true);
        showGiftPopup();
      } else if (cart.total_price < giftConfig.goalCents && alreadyUnlocked) {
        await setGiftAttribute(false);
      }
    } catch {} finally {
      giftCheckBusy = false;
    }
  }

  /* Covers Dawn's native product-form Add to Cart (not routed through addItemsToCart above) */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => checkFreeGift());
  }

  /* ──────────────────────────────────────────────────────────────────
     3. UPSELL PRODUCTS (max 3)
  ────────────────────────────────────────────────────────────────── */

  const LIMIT = 3;

  async function fetchByHandles(handles) {
    if (!handles?.length) return [];
    const results = await Promise.all(
      handles.slice(0, LIMIT).map(async h => {
        const handle = String(h).trim();
        if (!handle || handle.startsWith('gid://')) return null;
        try {
          const r = await fetch(`/products/${handle}.js`);
          return r.ok ? r.json() : null;
        } catch { return null; }
      })
    );
    return results.filter(Boolean).slice(0, LIMIT);
  }

  async function fetchCollectionProds(handle) {
    if (!handle) return [];
    try {
      const r = await fetch(`/collections/${handle}/products.json?limit=${LIMIT}`);
      if (!r.ok) return [];
      return ((await r.json()).products || []).slice(0, LIMIT);
    } catch { return []; }
  }

  function renderUpsell(products) {
    if (!upsellWrap || !upsellGrid || !products.length) return;
    upsellGrid.innerHTML = products.map(p => {
      const src   = getProductSrc(p);
      const price = getProductPrice(p);
      const vid   = p.variants?.[0]?.id || '';
      const avail = p.variants?.[0]?.available !== false;
      const multi = (p.variants?.length || 0) > 1;
      return `
        <div class="zr-atc-popup__upsell-item"
          data-product-id="${p.id}"
          data-product-title="${(p.title || '').replace(/"/g,'&quot;')}"
          data-product-url="/products/${p.handle}"
          data-product-image="${src}"
          data-product-price="${price}"
          data-variant-id="${vid}"
          data-has-multi-variants="${multi}"
          data-available="${avail}"
        >
          <a href="/products/${p.handle}" class="zr-atc-popup__upsell-img-wrap">
            ${src
              ? `<img class="zr-atc-popup__upsell-img" src="${src}" alt="${p.title}" loading="lazy">`
              : `<div class="zr-atc-popup__upsell-img zr-atc-popup__upsell-img--ph"></div>`}
          </a>
          <div class="zr-atc-popup__upsell-info">
            <a href="/products/${p.handle}" class="zr-atc-popup__upsell-name">${p.title}</a>
            <div class="zr-atc-popup__upsell-price">${price}</div>
            ${avail && !multi
              ? `<button class="zr-atc-popup__upsell-add" data-zr-quick-add type="button">
                   <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M8 3v10M3 8h10"/></svg>
                   Add
                 </button>`
              : multi
              ? `<a href="/products/${p.handle}" class="zr-atc-popup__upsell-add">View</a>`
              : `<span class="zr-atc-popup__upsell-add zr-atc-popup__upsell-add--sold">Sold out</span>`}
          </div>
        </div>`;
    }).join('');
    upsellWrap.hidden = false;
  }

  async function loadQuickAddUpsell(card) {
    if (!upsellWrap || !upsellGrid) return;
    upsellWrap.hidden = true;
    upsellGrid.innerHTML = '';

    /* 1. Product metafield handles */
    let handles = [];
    const raw = card?.dataset?.upsellProducts;
    if (raw && raw !== 'null' && raw !== '[]') {
      try { handles = JSON.parse(raw).filter(h => typeof h === 'string' && !h.startsWith('gid://')); }
      catch {}
    }
    if (handles.length) {
      const prods = await fetchByHandles(handles);
      if (prods.length) { renderUpsell(prods); return; }
    }

    /* 2. Collection fallback */
    const col = popup?.dataset?.offerCollection;
    if (col) {
      const prods = await fetchCollectionProds(col);
      if (prods.length) renderUpsell(prods);
    }
  }

  async function loadBundleUpsell() {
    if (!upsellWrap || !upsellGrid) return;
    upsellWrap.hidden = true;
    upsellGrid.innerHTML = '';
    const col = popup?.dataset?.offerCollection;
    if (!col) return;
    const prods = await fetchCollectionProds(col);
    if (prods.length) renderUpsell(prods);
  }

  /* ──────────────────────────────────────────────────────────────────
     4. QUICK ADD
  ────────────────────────────────────────────────────────────────── */

  async function handleQuickAdd(btn, card) {
    const variantId = card.dataset.variantId;
    if (!variantId || card.dataset.available !== 'true') return;

    btn.dataset.loading = '';
    const orig = btn.innerHTML;
    btn.innerHTML = `<svg class="zr-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-opacity=".25"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>`;

    try {
      await addItemsToCart([{ id: Number(variantId), quantity: 1 }]);
      refreshCartUI(); /* non-blocking — runs in background */
      checkFreeGift(); /* non-blocking — may override the popup below with the gift message */

      const inPopup = btn.closest('#zr-atc-popup');
      if (!inPopup) {
        fillProductSlot(
          card.dataset.productImage,
          card.dataset.productTitle || '',
          card.dataset.productPrice || '',
          card.dataset.comparePrice
        );
        upsellWrap && (upsellWrap.hidden = true);
        upsellGrid && (upsellGrid.innerHTML = '');
        openPopup('normal');
        loadQuickAddUpsell(card);
      } else {
        btn.innerHTML = `<svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="m3 8 4 4 6-7"/></svg>`;
        setTimeout(() => { btn.innerHTML = orig; }, 1400);
      }
    } catch {
      const url = card.dataset.productUrl;
      if (url) window.location.href = url;
    } finally {
      delete btn.dataset.loading;
      if (btn.innerHTML.includes('zr-spin')) btn.innerHTML = orig;
    }
  }

  /* ──────────────────────────────────────────────────────────────────
     5. BUNDLE ADD TO CART
  ────────────────────────────────────────────────────────────────── */

  async function handleBundleAdd(btn) {
    const section = btn.closest('[data-zr-bundle]');
    if (!section) return;
    const variantIds = [...section.querySelectorAll('[data-zr-bundle-variant]')]
      .map(el => el.dataset.zrBundleVariant).filter(Boolean);
    if (!variantIds.length) return;

    btn.dataset.loading = '';
    const orig = btn.innerHTML;
    btn.innerHTML = `<svg class="zr-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-opacity=".25"/><path d="M12 2a10 10 0 0 1 10 10"/></svg> Adding…`;

    try {
      let addedCount = 0;
      try {
        // Try batch add first (most efficient)
        await addItemsToCart(variantIds.map(id => ({ id: Number(id), quantity: 1 })));
        addedCount = variantIds.length;
      } catch {
        // Batch failed (422) — try adding items individually (some may be sold out)
        for (const vid of variantIds) {
          try {
            await addItemsToCart([{ id: Number(vid), quantity: 1 }]);
            addedCount++;
          } catch { /* skip unavailable variants */ }
        }
      }

      if (addedCount === 0) throw new Error('No items could be added');

      refreshCartUI();
      checkFreeGift();

      fillBundleSlot(addedCount);
      upsellWrap && (upsellWrap.hidden = true);
      upsellGrid && (upsellGrid.innerHTML = '');
      openPopup('bundle');
      loadBundleUpsell();

      btn.innerHTML = `<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="m3 8 4 4 6-7"/></svg> Added!`;
      btn.classList.add('zr-bundle-atc--success');
      setTimeout(() => { btn.innerHTML = orig; btn.classList.remove('zr-bundle-atc--success'); }, 2500);
    } catch {
      btn.innerHTML = 'Try again';
      setTimeout(() => { btn.innerHTML = orig; }, 2000);
    } finally {
      delete btn.dataset.loading;
    }
  }

  /* ──────────────────────────────────────────────────────────────────
     6. BUY NOW — inject button after ATC, add to cart then go to /checkout
  ────────────────────────────────────────────────────────────────── */

  function injectBuyNowButtons() {
    document.querySelectorAll('.product-form__buttons').forEach(wrap => {
      if (wrap.querySelector('[data-zr-buy-now]')) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('data-zr-buy-now', '');
      btn.className = 'zr-buy-now-btn';
      btn.textContent = 'Buy Now';
      wrap.appendChild(btn);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectBuyNowButtons);
  } else {
    injectBuyNowButtons();
  }

  /* ── Sticky Buy Now bar ─────────────────────────────────────────────
     Shows at the bottom when the main ATC/Buy Now buttons scroll out
     of view (user has passed them). Only active on product pages.
  ─────────────────────────────────────────────────────────────────── */
  function initStickyBar() {
    const bar      = document.getElementById('zr-sticky-bar');
    const titleEl  = document.getElementById('zr-sticky-bar-title');
    if (!bar || !titleEl) return;

    // Only run on product pages
    const productForm = document.querySelector('product-form');
    if (!productForm) return;

    // Pull product title from the PDP heading
    const title = (
      document.querySelector('.product__title')?.textContent ||
      document.querySelector('h1.product__title')?.textContent ||
      document.querySelector('.product__info-container h1')?.textContent ||
      ''
    ).trim();
    if (title) titleEl.textContent = title;

    bar.removeAttribute('aria-hidden');

    // Watch the .product-form__buttons wrapper
    const btnWrap = document.querySelector('.product-form__buttons');
    if (!btnWrap) return;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        // Buttons visible — hide bar
        bar.classList.remove('zr-sticky-bar--visible');
      } else {
        // Buttons out of view — only show when scrolled PAST them (not before)
        if (entry.boundingClientRect.top < 0) {
          bar.classList.add('zr-sticky-bar--visible');
        } else {
          bar.classList.remove('zr-sticky-bar--visible');
        }
      }
    }, { threshold: 0 });

    observer.observe(btnWrap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyBar);
  } else {
    initStickyBar();
  }

  async function handleBuyNow(btn) {
    const form = btn.closest('product-form')?.querySelector('form')
               || document.querySelector('product-form form');
    const variantId = form?.querySelector('[name="id"]')?.value;
    if (!variantId) { window.location = '/checkout'; return; }
    btn.disabled = true;
    btn.textContent = '…';
    try {
      await addItemsToCart([{ id: Number(variantId), quantity: 1 }]);
      await checkFreeGift();
      window.location = '/checkout';
    } catch {
      btn.disabled = false;
      btn.textContent = 'Buy Now';
    }
  }

  /* ──────────────────────────────────────────────────────────────────
     7. CART DRAWER QUANTITY / REMOVE
  ────────────────────────────────────────────────────────────────── */

  async function updateCartLine(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!res.ok) throw new Error('Cart update error');
    return res.json();
  }

  async function handleCartQtyChange(btn) {
    const delta = parseInt(btn.dataset.zrCdQtyChange, 10);
    const line  = parseInt(btn.dataset.line, 10);
    const input = btn.closest('[data-zr-cd-qty]')?.querySelector('.zr-cd__qty-val');
    if (!input || isNaN(line)) return;

    const current = parseInt(input.value, 10) || 1;
    const next    = Math.max(0, current + delta);

    /* Optimistic UI */
    input.value = next;
    btn.disabled = true;

    try {
      await updateCartLine(line, next);
      await refreshCartUI();
      await checkFreeGift();
    } catch {
      input.value = current;
    } finally {
      btn.disabled = false;
    }
  }

  async function handleCartRemove(btn) {
    const line = parseInt(btn.dataset.line, 10);
    if (isNaN(line)) return;

    const item = btn.closest('.zr-cd__item');
    if (item) item.style.opacity = '.4';

    try {
      await updateCartLine(line, 0);
      await refreshCartUI();
      await checkFreeGift();
    } catch {
      if (item) item.style.opacity = '';
    }
  }

  /* Upsell carousel navigation */
  function initUpsellCarousel() {
    const track = document.querySelector('[data-zr-cd-upsell-track]');
    const prev  = document.querySelector('[data-zr-cd-upsell-prev]');
    const next  = document.querySelector('[data-zr-cd-upsell-next]');
    if (!track) return;

    let idx = 0;
    const cards = () => track.querySelectorAll('.zr-cd__upsell-card');

    function slide(dir) {
      const c = cards();
      if (!c.length) return;
      idx = Math.max(0, Math.min(idx + dir, c.length - 1));
      const offset = c[idx].offsetLeft - track.offsetLeft;
      track.scrollTo({ left: offset, behavior: 'smooth' });
      if (prev) prev.disabled = idx === 0;
      if (next) next.disabled = idx >= c.length - 1;
    }

    if (prev) prev.addEventListener('click', () => slide(-1));
    if (next) next.addEventListener('click', () => slide(1));
    /* Initial state */
    if (prev) prev.disabled = true;
    if (next && cards().length <= 1) next.disabled = true;
  }

  /* Rebind carousel after cart refresh */
  const _origRefresh = refreshCartUI;
  refreshCartUI = async function() {
    await _origRefresh();
    initUpsellCarousel();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsellCarousel);
  } else {
    initUpsellCarousel();
  }

  /* ──────────────────────────────────────────────────────────────────
     8. EVENT DELEGATION
  ────────────────────────────────────────────────────────────────── */

  document.addEventListener('click', async (e) => {
    /* Cart drawer qty +/- */
    const qtyBtn = e.target.closest('[data-zr-cd-qty-change]');
    if (qtyBtn) { e.preventDefault(); await handleCartQtyChange(qtyBtn); return; }
    /* Cart drawer remove */
    const rmBtn = e.target.closest('[data-zr-cd-remove]');
    if (rmBtn) { e.preventDefault(); await handleCartRemove(rmBtn); return; }
    /* Quick Add */
    const qaBtn = e.target.closest('[data-zr-quick-add]');
    if (qaBtn) { e.preventDefault(); const c = qaBtn.closest('[data-product-id]'); if (c) await handleQuickAdd(qaBtn, c); return; }
    const bundleBtn = e.target.closest('[data-zr-bundle-add]');
    if (bundleBtn) { e.preventDefault(); await handleBundleAdd(bundleBtn); return; }
    const buyNowBtn = e.target.closest('[data-zr-buy-now]');
    if (buyNowBtn) { e.preventDefault(); await handleBuyNow(buyNowBtn); return; }
  });

  closeBtn?.addEventListener('click', closePopup);
  continueBtn?.addEventListener('click', closePopup);
  backdrop?.addEventListener('click', closePopup);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && popup && !popup.hidden) closePopup(); });

  /* ──────────────────────────────────────────────────────────────────
     9. STICKY NAV
  ────────────────────────────────────────────────────────────────── */

  window.addEventListener('scroll', () => {
    const atTop = window.scrollY <= 60;
    if (atTop) document.documentElement.classList.remove('zr-scrolled-up');
    else document.documentElement.classList.toggle('zr-scrolled-up', (window._zrLastScroll || 0) > window.scrollY);
    window._zrLastScroll = window.scrollY;
  }, { passive: true });

})();
